import { R2_WORKER_BASE_URL, R2_PUBLIC_BASE_URL } from '../config/r2Config';

/**
 * Calls the Worker's /presigned-upload-url endpoint.
 * @param {string} filename - The name of the file to upload.
 * @param {string} contentType - The MIME type of the file.
 * @returns {Promise<{uploadUrl: string, objectKey: string}>}
 */
export async function getPresignedUploadUrl(filename, contentType) {
  try {
    const response = await fetch(`${R2_WORKER_BASE_URL}/presigned-upload-url`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ filename, contentType }),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get presigned URL: ${response.status} ${errorText}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error getting presigned upload URL:', error);
    throw error;
  }
}

/**
 * Uploads a file directly to R2 via the worker endpoint.
 * @param {File} file - The file to upload.
 * @param {function(progress: number): void} [onProgress] - Optional callback for progress updates (0-100).
 * @returns {Promise<{url: string, objectKey: string}>}
 */
export async function uploadFileToR2(file, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const formData = new FormData();
    formData.append('file', file);
    
    xhr.open('POST', `${R2_WORKER_BASE_URL}/upload`, true);

    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = (event.loaded / event.total) * 100;
          onProgress(percentComplete);
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        if (onProgress) onProgress(100); // Ensure 100% progress on completion
        try {
          const response = JSON.parse(xhr.responseText);
          resolve(response);
        } catch (err) {
          reject(new Error('Invalid response format'));
        }
      } else {
        reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
      }
    };

    xhr.onerror = () => {
      reject(new Error('Upload failed due to network error.'));
    };

    xhr.send(formData);
  });
}

/**
 * Calls the Worker's /object endpoint to delete an object.
 * @param {string} objectKey - The key of the object to delete.
 * @returns {Promise<Response>}
 */
export async function deleteR2Object(objectKey) {
  try {
    const response = await fetch(`${R2_WORKER_BASE_URL}/object`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ objectKey }),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to delete object: ${response.status} ${errorText}`);
    }
    return response;
  } catch (error) {
    console.error('Error deleting R2 object:', error);
    throw error;
  }
}

/**
 * Calls the Worker's /move-object endpoint.
 * @param {string} sourceKey - The source key of the object.
 * @param {string} destinationKey - The destination key for the object.
 * @returns {Promise<Response>}
 */
export async function moveR2Object(sourceKey, destinationKey) {
  try {
    const response = await fetch(`${R2_WORKER_BASE_URL}/move-object`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ sourceKey, destinationKey }),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to move object: ${response.status} ${errorText}`);
    }
    return response;
  } catch (error) {
    console.error('Error moving R2 object:', error);
    throw error;
  }
}

/**
 * Calls the Worker's /list-objects endpoint.
 * @param {string} [prefix=''] - Optional prefix to filter objects.
 * @returns {Promise<any>}
 */
export async function listR2Objects(prefix = '') {
  try {
    const url = new URL(`${R2_WORKER_BASE_URL}/list-objects`);
    if (prefix) {
      url.searchParams.append('prefix', prefix);
    }
    const response = await fetch(url.toString());
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to list objects: ${response.status} ${errorText}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error listing R2 objects:', error);
    throw error;
  }
}

/**
 * Constructs the public URL for an R2 object.
 * @param {string} objectKey - The key of the R2 object.
 * @returns {string} The public URL.
 */
export function getR2PublicUrl(objectKey) {
  if (!objectKey) return '';
  // Ensure no double slashes if R2_PUBLIC_BASE_URL ends with / and objectKey starts with /
  const baseUrl = R2_PUBLIC_BASE_URL.endsWith('/') ? R2_PUBLIC_BASE_URL.slice(0, -1) : R2_PUBLIC_BASE_URL;
  const key = objectKey.startsWith('/') ? objectKey.slice(1) : objectKey;
  return `${baseUrl}/${key}`;
}
